import { SingleSelect, Label, Button, Modal } from "@/vendor/Forge/dist";
import { styled, globalCss } from "@stitches/react";
import { useTaoDepartments } from "@/hooks/use-tao-departments";
import { useTaoGenuses } from "@/hooks/use-tao-genuses";
import { useTaoOrderTypes } from "@/hooks/use-tao-order-types";
import { TaoDepartmentsList } from "@/types/tao-routing";
import { useTaoProviders } from "@/hooks/use-tao-providers";
import { useTaoRulesContext } from "@/contexts/tao-rule-context";
import { MultiselectChangeEvent, StructuredOption } from "@/vendor/Forge/dist";
import { useEffect, useState } from "react";
import { TaoHistoryByIdManager, TaoHistoryManager } from "../TaoUserManager";

const globalStyles = globalCss({
  '[data-testid="overall-tao-history-modal"] .fe_c_modal__dialog--large': {
    width: "896px !important",
    maxWidth: "90vw !important",
    marginLeft: "auto !important",
    marginRight: "auto !important",
    left: "0 !important",
    right: "0 !important",
  },
});
globalStyles();

const Wrapper = styled("div", {
  display: "flex",
  marginLeft: "32px",
  marginTop: "10px",
  justifyContent: "space-between",

  "& .fe_c_radio-button": {
    marginTop: "10px",
    marginRight: "32px",
  },
});

const ActionsWrapper = styled("div", {
  display: "flex",
  justifyContent: "right",
  alignItems: "center",
  gap: "15px",

  "& .fe_c_button": {
    marginTop: "10px",
  },
});

const FieldsWrapper = styled("div", {
  display: "flex",
  gap: "24px",
});

const FieldContent = styled("div", {
  display: "flex",
  flexFlow: "column",

  "& p": {
    marginTop: "7px",
    fontWeight: "bold",
  },
  "& .fe_c_multiselect": {
    minWidth: "149px",
  },
});

interface Props {
  isTaoAdmin: boolean | undefined;
  setShowTaoHistoryByIdModal: (data: boolean) => void;
  selectedTaoHistoryById: number;
  showTaoHistoryByIdModal: boolean;
}

const TaoRulesFilters = ({
  showTaoHistoryByIdModal,
  selectedTaoHistoryById,
  setShowTaoHistoryByIdModal,
  isTaoAdmin,
}: Props) => {
  const { data: orderTypes, isLoading: isOrderTypesLoading } =
    useTaoOrderTypes();
  const { data: departments, isLoading: isDepartmentsLoading } =
    useTaoDepartments();
  const { data: genuses, isLoading: isGenusesLoading } = useTaoGenuses();
  const { data: providers, isLoading: isProvidersLoading } = useTaoProviders();
  const [overallTaoHistoryModal, setOverallTaoHistoryModal] = useState(false);
  const { updateFilters, filters } = useTaoRulesContext();
  const [selectedOrderType, setSelectedOrderType] = useState<string>();
  const [selectedProvider, setSelectedProvider] = useState<string>();
  const [selectedDepartment, setSelectedDepartment] = useState<string>();

  const showOverallTaoHistoryModal = () => {
    setOverallTaoHistoryModal(true);
  };

  useEffect(() => {
    if (orderTypes) {
      const selectedOrderTypeNameFilter = orderTypes?.filter(
        (orderType) => orderType.orderTypeId == filters?.orderTypeIds[0],
      );
      setSelectedOrderType(selectedOrderTypeNameFilter[0]?.orderTypeName);
    }
  }, [orderTypes, filters]);

  useEffect(() => {
    if (providers) {
      const selectedProviderNameFilter = providers.providers?.filter(
        (provider) => provider.providerId == filters?.providerIds[0],
      );
      setSelectedProvider(selectedProviderNameFilter[0]?.displayName);
    }
  }, [providers, filters]);

  useEffect(() => {
    if (departments) {
      if (departments.departments === null) {
        return setSelectedDepartment("All"); // handle case when departments are null
      } else {
        const selectedDepartmentNameFilter = departments.departments?.filter(
          (department) => department.departmentId == filters?.departmentIds[0],
        );
        setSelectedDepartment(selectedDepartmentNameFilter[0]?.name);
      }
    }
  }, [departments, filters]);

  return (
    <Wrapper>
      <FieldsWrapper>
        <FieldContent>
          <Label text="Departments" aria-label="departments-label" />
          <SingleSelect
            placeholder="All"
            data-testid="departments-filter"
            options={
              isDepartmentsLoading == true
                ? []
                : [
                    "All",
                    ...(departments?.departments?.map(
                      (department: TaoDepartmentsList) => department.name,
                    ) ?? []),
                  ]
            }
            aria-labelledby="departments-label"
            noOptionsMessage="Loading..."
            value={selectedDepartment}
            onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
              const selected = e.target.value?.value as string;
              const selectedDepartment = departments?.departments?.filter(
                (department) => department.name.includes(selected),
              );
              if (selected == "All") {
                return updateFilters({
                  departments: [],
                  departmentIds: [],
                });
              }
              if (selectedDepartment) {
                return updateFilters({
                  departmentIds: [selectedDepartment[0].departmentId],
                });
              }
            }}
          />
        </FieldContent>
        <FieldContent>
          <Label text="Providers" aria-label="providers-label" />
          <SingleSelect
            placeholder="All"
            options={
              isProvidersLoading == true
                ? []
                : [
                    "All",
                    ...(providers?.providers.map(
                      (provider) => provider.displayName,
                    ) ?? []),
                  ]
            }
            aria-labelledby="providers-label"
            noOptionsMessage="Loading..."
            value={selectedProvider}
            onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
              const selected = e.target.value?.value as string;
              const selectedProvider = providers?.providers?.filter(
                (provider) => provider.displayName.includes(selected),
              );
              if (selected == "All") {
                return updateFilters({
                  providers: [],
                  providerIds: [],
                });
              }
              if (selectedProvider) {
                return updateFilters({
                  providerIds: [selectedProvider[0].providerId],
                });
              }
            }}
          />
        </FieldContent>
        <FieldContent>
          <Label text="Genus" aria-label="genus-label" />
          <SingleSelect
            placeholder="All"
            options={
              isGenusesLoading == true ? [] : ["All", ...(genuses ?? [])]
            }
            aria-labelledby="genus-label"
            noOptionsMessage="Loading..."
            value={filters.orderGenusList[0]}
            onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
              const selected = e.target.value?.value as string;
              if (selected == "All") {
                return updateFilters({
                  orderGenusList: [],
                });
              }
              return updateFilters({
                orderGenusList: [selected],
              });
            }}
          />
        </FieldContent>
        <FieldContent>
          <Label text="Order Type" aria-label="order-type-label" />
          <SingleSelect
            placeholder="All"
            options={
              isOrderTypesLoading == true
                ? []
                : [
                    "All",
                    ...(orderTypes?.map(
                      (orderType) => orderType.orderTypeName,
                    ) ?? []),
                  ]
            }
            aria-labelledby="order-type-label"
            noOptionsMessage="Loading..."
            value={selectedOrderType}
            onChange={(e: MultiselectChangeEvent<StructuredOption, false>) => {
              const selected = e.target.value?.value as string;
              const selectedOrderTypeId = orderTypes?.filter((orderType) =>
                orderType.orderTypeName.includes(selected),
              );
              if (selected == "All") {
                setSelectedOrderType(selected);
                return updateFilters({
                  orderTypes: [],
                  orderTypeIds: [],
                });
              }
              if (selectedOrderTypeId) {
                setSelectedOrderType(selected);
                return updateFilters({
                  orderTypeIds: [selectedOrderTypeId[0].orderTypeId],
                });
              }
            }}
          />
        </FieldContent>
      </FieldsWrapper>
      <ActionsWrapper>
        {!isTaoAdmin ? (
          <Button
            variant="secondary"
            data-testid="view-change-history-button"
            onClick={showOverallTaoHistoryModal}
          >
            View Change History
          </Button>
        ) : (
          <></>
        )}
        {/* <RadioButton
                name="bulkActionIdentifierSelect"
                data-testid="radioButton"
                options={[
                    { label: "All", value: "all" },
                    { label: "Active", value: "active" },
                ]}
                onChange={() => console.log("banana")}
                value="all"/> */}
      </ActionsWrapper>

      <Modal
        data-testid="overall-tao-history-modal"
        headerText="Overall Tao Change History"
        show={overallTaoHistoryModal}
        includeButtonRow={false}
        width="large"
        onHide={() => {
          setOverallTaoHistoryModal(false);
        }}
        onBackdropClick={() => {
          setOverallTaoHistoryModal(false);
        }}
        onPrimaryClick={() => {
          setOverallTaoHistoryModal(false);
        }}
      >
        <TaoHistoryManager />
      </Modal>
      <Modal
        data-testid="tao-history-modal-by-id"
        headerText={`TAO ${selectedTaoHistoryById} Change History Log`}
        show={showTaoHistoryByIdModal}
        includeButtonRow={false}
        width="large"
        onHide={() => {
          setShowTaoHistoryByIdModal(false);
        }}
        onBackdropClick={() => {
          setShowTaoHistoryByIdModal(false);
        }}
        onPrimaryClick={() => {
          setShowTaoHistoryByIdModal(false);
        }}
      >
        <TaoHistoryByIdManager selectedTaoId={selectedTaoHistoryById} />
      </Modal>
    </Wrapper>
  );
};

export default TaoRulesFilters;
